import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  Link,
} from '@mui/material';
import { Business, Email, Lock } from '@mui/icons-material';

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onShowRegistration: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onShowRegistration }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // バリデーション
    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください');
      setLoading(false);
      return;
    }

    if (!email.includes('@')) {
      setError('正しいメールアドレスを入力してください');
      setLoading(false);
      return;
    }

    try {
      await onLogin(email, password);
    } catch (error: any) {
      console.error('Login error details:', error);
      if (error?.message) {
        setError(`ログインエラー: ${error.message}`);
      } else if (error?.error_description) {
        setError(`認証エラー: ${error.error_description}`);
      } else {
        setError('ログインに失敗しました。メールアドレスとパスワードを確認してください。');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* ロゴ・タイトル */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Business sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            マンション管理組合
          </Typography>
          <Typography variant="h5" component="h2" color="text.secondary">
            家計簿システム
          </Typography>
        </Box>

        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" component="h3" gutterBottom textAlign="center">
              ログイン
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              {/* メールアドレス */}
              <TextField
                fullWidth
                label="メールアドレス"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                placeholder="your-email@mansion.com"
              />

              {/* パスワード */}
              <TextField
                fullWidth
                label="パスワード"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              {/* ログインボタン */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ mb: 3 }}
              >
                {loading ? 'ログイン中...' : 'ログイン'}
              </Button>

              {/* リンク */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link href="#" variant="body2" sx={{ mr: 2 }}>
                  パスワードを忘れた方
                </Link>
                <Link 
                  component="button" 
                  variant="body2" 
                  onClick={(e) => {
                    e.preventDefault();
                    onShowRegistration();
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  新規登録
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;